import { useEffect, useState } from "react";
import { Landmark, FileText, Percent, BookOpen, CalendarClock } from "lucide-react";
import { api, fmtDate, fmtMoney } from "../../lib/api";
import { PageHeader, DataTable, Td, StatusBadge, EmptyState, ActionButton } from "../../components/common";
import { ProformaList } from "../../components/accounting/ProformaList";
import { PeriodicBilling } from "../../components/accounting/PeriodicBilling";

const TABS = [
  { key: "invoicing", label: "Invoicing", icon: Landmark },
  { key: "periodic", label: "Periodic Billing", icon: CalendarClock },
];

const InvoicingPanel = () => {
  const [lines, setLines] = useState(null);

  useEffect(() => {
    api.get("/accounting/preview").then((r) => setLines(r.data)).catch(() => setLines([]));
  }, []);

  const totalReady = (lines || []).reduce((s, l) => s + (l.amount || 0), 0);

  return (
    <div data-testid="accounting-invoicing-panel">
      <div className="bg-amber-500/5 border border-amber-500/30 rounded-lg px-5 py-4 mb-8" data-testid="accounting-combine-note">
        <p className="text-sm text-amber-300/90 leading-relaxed">
          <span className="font-mono uppercase tracking-widest text-xs text-amber-500 block mb-1">Planned billing rule</span>
          Users will be able to combine all Batches and Sales Orders they choose — from any of the Logic modules (Hauling Logic, Rental Logic, and future modules) — to bill a customer in a single invoice.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-8">
        {[
          { icon: FileText, title: "Invoice Generation", desc: "Auto-build customer invoices from confirmed SWD barrel orders and approved frac tank rentals, with per-customer formats." },
          { icon: Percent, title: "Rate Sheets & Tax", desc: "Per-customer rate sheets, disposal tax handling, and configurable invoice numbering sequences." },
          { icon: BookOpen, title: "Billing Ledger", desc: "Posted invoices, aging, and payment status per customer — tied to the shared customer ledger." },
        ].map((c) => (
          <div key={c.title} className="bg-card border border-dashed border-slate-600 rounded-lg p-6" data-testid={`accounting-feature-${c.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <c.icon size={20} className="text-indigo-400 mb-3" />
            <h3 className="font-heading text-xl font-semibold text-slate-100">{c.title}</h3>
            <p className="text-sm text-slate-400 mt-2 leading-relaxed">{c.desc}</p>
          </div>
        ))}
      </div>

      <ProformaList />

      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-slate-400">Invoice-Ready Revenue (preview)</p>
          <p className="font-heading text-3xl font-bold text-amber-400" data-testid="accounting-ready-total">{fmtMoney(totalReady)}</p>
        </div>
        <ActionButton testId="accounting-generate-invoice-btn" disabled title="Available when the Accounting module ships">
          <Landmark size={16} /> Generate Invoices
        </ActionButton>
      </div>

      {lines === null ? (
        <EmptyState message="Loading preview…" testId="accounting-loading" />
      ) : lines.length === 0 ? (
        <EmptyState message="No confirmed or approved orders ready to invoice" testId="accounting-empty" />
      ) : (
        <DataTable
          testId="accounting-preview-table"
          columns={["Source Module", "Order", "Customer", "Amount", "Order Status", "Date"]}
          rows={lines}
          renderRow={(l) => (
            <tr key={`${l.order_number}-${l.source}`} className="hover:bg-slate-800/30 transition-colors duration-150" data-testid={`invoice-line-${l.order_number}`}>
              <Td>
                <span className="px-2 py-0.5 rounded bg-slate-800 border border-slate-700 text-xs font-mono text-slate-300">{l.source}</span>
              </Td>
              <Td className="font-mono text-amber-400">{l.order_number}</Td>
              <Td className="font-medium text-slate-100">{l.customer}</Td>
              <Td className="font-mono text-emerald-400">{fmtMoney(l.amount)}</Td>
              <Td><StatusBadge status={l.status} /></Td>
              <Td className="text-slate-500 text-xs">{fmtDate(l.created_at)}</Td>
            </tr>
          )}
        />
      )}
    </div>
  );
};

export default function Accounting() {
  const [tab, setTab] = useState("invoicing");

  return (
    <div data-testid="accounting-page">
      <PageHeader title="Accounting Logic" subtitle="Invoicing engine — module scaffolding. Customer billing formats land here next." testId="accounting-header">
        <span className="px-3 py-1.5 rounded-md bg-indigo-500/10 border border-indigo-500/30 text-indigo-400 text-xs font-mono uppercase tracking-wider" data-testid="accounting-scaffold-badge">
          Module Scaffolding
        </span>
      </PageHeader>

      <div className="flex gap-1 border-b border-slate-700 mb-8" data-testid="accounting-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`accounting-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors duration-150 ${
              tab === t.key ? "border-amber-400 text-amber-400" : "border-transparent text-slate-400 hover:text-slate-200"
            }`}
          >
            <t.icon size={16} /> {t.label}
          </button>
        ))}
      </div>

      {tab === "invoicing" ? <InvoicingPanel /> : <PeriodicBilling />}
    </div>
  );
}
